import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { Toaster } from './components/ui/sonner';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardLayout from './pages/DashboardLayout';
import ExecutiveDashboard from './pages/ExecutiveDashboard';
import InventoryPage from './pages/InventoryPage';
import AgentDetailPage from './pages/AgentDetailPage';
import ViolationsPage from './pages/ViolationsPage';
import PoliciesPage from './pages/PoliciesPage';
import KillSwitchPage from './pages/KillSwitchPage';
import AuditPage from './pages/AuditPage';
import GraphPage from './pages/GraphPage';
import SecurityPage from './pages/SecurityPage';
import PilotRequestPage from './pages/PilotRequestPage';
import OnePagerPage from './pages/OnePagerPage';
import IncidentCenter from './pages/IncidentCenter';
import FirstScanPage from './pages/FirstScanPage';
import DeploymentGatePage from './pages/DeploymentGatePage';
import ConnectorsPage from './pages/ConnectorsPage';
import PolicySimulatorPage from './pages/PolicySimulatorPage';
import ComplianceDashboard from './pages/ComplianceDashboard';
import PilotRequestsAdmin from './pages/PilotRequestsAdmin';

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-[#9CA3AF]">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-[#9CA3AF]">Loading...</div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        }
      />

      {/* Pilot Request - Public */}
      <Route path="/request-pilot" element={<PilotRequestPage />} />

      {/* One-Pager - Public */}
      <Route path="/one-pager" element={<OnePagerPage />} />

      {/* Protected Dashboard Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<ExecutiveDashboard />} />
        <Route path="executive" element={<ExecutiveDashboard />} />
        <Route path="inventory" element={<InventoryPage />} />
        <Route path="agents/:agentId" element={<AgentDetailPage />} />
        <Route path="graph" element={<GraphPage />} />
        <Route path="violations" element={<ViolationsPage />} />
        <Route path="incidents" element={<IncidentCenter />} />
        <Route path="policies" element={<PoliciesPage />} />
        <Route path="killswitch" element={<KillSwitchPage />} />
        <Route path="audit" element={<AuditPage />} />
        <Route path="security" element={<SecurityPage />} />
        <Route path="first-scan" element={<FirstScanPage />} />
        <Route path="deployment-gate" element={<DeploymentGatePage />} />
        <Route path="connectors" element={<ConnectorsPage />} />
        <Route path="policy-simulator" element={<PolicySimulatorPage />} />
        <Route path="compliance" element={<ComplianceDashboard />} />
        <Route path="leads" element={<PilotRequestsAdmin />} />
      </Route>

      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" />
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
